import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import QRCode from 'react-qr-code';
import { useSelector, useDispatch } from 'react-redux';

import { Edit, Visibility } from '@material-ui/icons';
import { parseISO, format } from 'date-fns';
import { assign } from 'lodash';

import FotoIcon from '~/assets/icons/foto-icon.svg';
import QrIcon from '~/assets/icons/qrcode-icon.svg';
import { UnidataTable } from '~/components';
import { SKID } from '~/constants/modules';
import { t, can } from '~/helpers';
import { handleExportGeneric } from '~/helpers/exportReports';
import dateTime from '~/hooks/DateTime';
import {
  deleteData,
  findData,
  recordFiltering,
} from '~/store/modules/operacaoRecebimentoNf/actions';

import { Heading, Form } from './components';
import QRCodeDialog from './components/QRCodeDialog';
import QRFotoDialog from './components/QRFotoDialog';

function OperacaoRecebimentoNf() {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const redux = useSelector((state) => state.operacaoRecebimentoNf);

  const buttons = [];

  const [serverDate, setServerDate] = useState(null);
  const [modal, setModal] = useState(false);
  const [params, setParams] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  const [iddescarganf, setIddescarganf] = useState('');
  const [idposto, setIdposto] = useState('');

  const [isFinalizado, setIsFinalizado] = useState(false);

  const [openedFotoQrCodeNF, setOpenedFotoQrCodeNF] = useState(false);
  const [openedQrCodeNF, setOpenedQrCodeNF] = useState(false);
  const [tokenUuidDescarganf, setTokenUuidDescarganf] = useState('');
  const [
    habilitafotonotafiscalposto,
    setHabilitafotonotafiscalposto,
  ] = useState('');
  const [jatiroufotodescarganf, setJatiroufotodescarganf] = useState('');
  const [nfdescarganf, setNfdescarganf] = useState('');

  const token = useSelector((state) => state.auth);

  const appUrl = `${
    window.location.host
  }/fotonf?id=${tokenUuidDescarganf}&token=${JSON.stringify(
    token.token
  ).replace(/^["']|["']$/g, '')}`;

  const status = useMemo(
    () =>
      can(SKID)
        ? [
            { label: 0, value: t('s.operacao.recebimento.andamento') },
            { label: 1, value: t('s.operacao.recebimento.finalizado') },
            { label: 2, value: t('s.operacao.recebimento.cancelamento') },
          ]
        : [
            { label: 0, value: t('s.operacao.recebimento.andamento') },
            { label: 1, value: t('s.operacao.recebimento.finalizado') },
          ],
    []
  );

  const header = useMemo(() => {
    const data = [
      {
        id: 'datahorainiciodescarganf',
        label: t('s.operacao.recebimento.datahorainiciodescarga'),
        formatted: (value) => format(parseISO(value), 'dd/MM/yyyy HH:mm'),
        filter: {
          type: 'initialdatetime',
          defaultValue: serverDate
            ? serverDate
                .set({ hour: 0, minute: 0, second: 0 })
                .format('DD/MM/YYYY HH:mm:ss')
            : '',
        },
      },
      {
        id: 'datahorafimdescarganf',
        label: t('s.operacao.recebimento.datahorafimdescarga'),
        formatted: (value) =>
          value ? format(parseISO(value), 'dd/MM/yyyy HH:mm') : '',
        filter: {
          type: 'finaldatetime',
          defaultValue: serverDate
            ? serverDate
                .set({ hour: 23, minute: 59, second: 59 })
                .format('DD/MM/YYYY HH:mm:ss')
            : '',
        },
      },
      {
        id: 'codigonomeposto',
        label: t('s.operacao.recebimento.nomeposto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'nomeproduto',
        label: t('s.menu.configuracao.produto'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'nfdescarganf',
        label: t('s.operacao.recebimento.nfdescarga'),
        filter: {
          type: 'text',
        },
        align: 'right',
      },
      {
        id: 'finalizadadescarganf',
        label: t('s.configuracao.posto.statusposto'),
        formatted: (value) => status.find((s) => s.label === value).value ?? '',
        filter: {
          type: 'select',
          options: status,
          defaultValue: '0',
        },
      },
      {
        id: 'volumenfdescarganf',
        label: t('s.operacao.recebimento.volumenfdescarga'),
        filter: {
          type: 'number',
        },
        align: 'right',
      },
      {
        id: 'placactdescarganf',
        label: t('s.operacao.recebimento.placactdescarga'),
        filter: {
          type: 'text',
        },
      },
      {
        id: 'codigousuario',
        label: t('s.operacao.recebimento.codigousuario'),
        filter: {
          type: 'text',
        },
      },
    ];

    if (can(SKID)) {
      data.splice(9, 0, {
        id: 'mensagemerrodescarganf',
        label: t('s.menu.cadastro.mensagem'),
        filter: { type: 'text' },
        align: 'left',
      });
    }

    return data;
  }, [serverDate, status]);

  const fieldlist = [
    'datahorainiciodescarganf',
    'datahorafimdescarganf',
    'codigonomeposto',
    'nomeproduto',
    'nfdescarganf',
    'finalizadadescarganf',
    'volumenfdescarganf',
    'placactdescarganf',
    'codigousuario',
  ];

  const substituirHeader = {
    datahorainiciodescarganf: t(
      's.operacao.recebimento.datahorainiciodescarga'
    ),
    datahorafimdescarganf: t('s.operacao.recebimento.datahorafimdescarga'),
    codigonomeposto: t('s.operacao.recebimento.nomeposto'),
    nomeproduto: t('s.menu.configuracao.produto'),
    nfdescarganf: t('s.operacao.recebimento.nfdescarga'),
    finalizadadescarganf: t('s.configuracao.posto.statusposto'),
    volumenfdescarganf: t('s.operacao.recebimento.volumenfdescarga'),
    placactdescarganf: t('s.operacao.recebimento.placactdescarga'),
    codigousuario: t('s.operacao.recebimento.codigousuario'),
  };

  if (can(SKID)) {
    fieldlist.push('mensagemerrodescarganf');
    substituirHeader.mensagemerrodescarganf = t('s.menu.cadastro.mensagem');
  }

  // Open Modal Edit
  const handleOpenModalEdit = useCallback((values) => {
    setModal(true);
    setParams(values);
  }, []);

  // Close Modal Edit
  const handleCloseModalEdit = useCallback(() => {
    setModal(false);
    setParams({});

    dispatch(recordFiltering(redux.params));
  }, [redux]);

  const handleToggleQRcodeNFOpen = () => {
    setOpenedQrCodeNF((value) => !value);
  };

  const handleToggleFotoQRcodeNFOpen = () => {
    setOpenedFotoQrCodeNF((value) => !value);
    dispatch(recordFiltering(assign(redux?.params, params)));
  };

  const actionsButtons = useMemo(() => {
    const buttons = [];

    isFinalizado &&
      buttons.push({
        title: t('s.geral.visualizar'),
        action: (value) => handleOpenModalEdit(value),
        icon: <Visibility />,
      });

    isUpdate &&
      buttons.push({
        title: t('s.geral.botaoalterar'),
        action: (value) => handleOpenModalEdit(value),
        icon: <Edit />,
      });

    // isUpdate &&
    //   habilitafotonotafiscalposto === 'S' &&
    //   jatiroufotodescarganf === 'N' &&
    //   buttons.push({
    //     title: t('QR-Code Nota Fiscal'),
    //     action: () => handleToggleQRcodeNFOpen(),
    //     icon: <img src={QrIcon} />,
    //   })

    // isUpdate &&
    //   habilitafotonotafiscalposto === 'S' &&
    //   jatiroufotodescarganf === 'S' &&
    //   buttons.push({
    //     title: t('Fotos Nota Fiscal'),
    //     action: () => handleToggleFotoQRcodeNFOpen(),
    //     icon: <img src={FotoIcon} />,
    //   })

    // !isUpdate &&
    //   habilitafotonotafiscalposto === 'S' &&
    //   jatiroufotodescarganf === 'S' &&
    //   buttons.push({
    //     title: t('Fotos Nota Fiscal'),
    //     action: () => handleToggleFotoQRcodeNFOpen(),
    //     icon: <img src={FotoIcon} />,
    //   })

    return buttons;
  }, [
    isUpdate,
    isFinalizado,
    habilitafotonotafiscalposto,
    jatiroufotodescarganf,
  ]);

  const actions = useMemo(
    () =>
      isUpdate
        ? [
            {
              title: t('s.geral.botaoalterar'),
              action: (value) => handleOpenModalEdit(value),
              icon: <Edit />,
            },
            // ,{
            //   title: t('QR NF'),
            //   action: () => handleToggleQRcodeNFOpen(),
            //   icon: <img src={QrIcon} />,
            // },
          ]
        : [],
    [isUpdate, habilitafotonotafiscalposto]
  );

  useEffect(() => {
    async function handleGetDate() {
      const date = await dateTime();
      setServerDate(date);
    }

    handleGetDate();
  }, []);

  useEffect(() => {
    if (modal) {
      formRef.current.openModal();
    }
  }, [modal]);

  return (
    <>
      {/* ({JSON.stringify(appUrl)})
      ({JSON.stringify(tokenUuidDescarganf)}) */}

      {serverDate && (
        <UnidataTable
          fixed
          create={false}
          update={false}
          customActionsFirst
          redux={redux}
          header={header}
          read={recordFiltering}
          find={findData}
          remove={deleteData}
          toolbar={Heading}
          actions={actionsButtons}
          // actions={actions}
          detach="nomeposto"
          keys={['iddescarganf', 'idposto']}
          onRow={({
            iddescarganf,
            idposto,
            finalizadadescarganf,
            tokenuuiddescarganf,
            habilitafotonotafiscalposto,
            jatiroufotodescarganf,
            nfdescarganf,
          }) => {
            setIsUpdate(Number(finalizadadescarganf) === 0);

            setIsFinalizado(Number(finalizadadescarganf) === 1);

            setTokenUuidDescarganf(tokenuuiddescarganf);
            setHabilitafotonotafiscalposto(habilitafotonotafiscalposto);
            setJatiroufotodescarganf(jatiroufotodescarganf);
            setIddescarganf(iddescarganf);
            setIdposto(idposto);
            setNfdescarganf(nfdescarganf);
          }}
          pdf
          excel
          onExport={(formato) =>
            handleExportGeneric({
              endpoint: 'descargasnfs',
              formato,
              fields: fieldlist,
              headers: substituirHeader,
              redux,
              ordination: redux.params.ordination,
              orderfield: redux.params.orderfield,
              title: 'Relatório de Recebimento NF',
              substituirValores: {
                finalizadadescarganf: {
                  '0': t('s.operacao.recebimento.andamento'),
                  '1': t('s.operacao.recebimento.finalizado'),
                  '2': t('s.operacao.recebimento.cancelamento'),
                },
              },
            })
          }
        />
      )}

      {modal && (
        <Form
          ref={formRef}
          onCloseModal={handleCloseModalEdit}
          formEdit={params}
        />
      )}

      <QRCodeDialog
        open={openedQrCodeNF}
        handleClose={handleToggleQRcodeNFOpen}
        title="Qr Code NF"
        children={
          <QRCode
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            value={`${appUrl}`}
            viewBox="0 0 256 256"
          />
        }
      />

      <QRFotoDialog
        open={openedFotoQrCodeNF}
        handleClose={handleToggleFotoQRcodeNFOpen}
        title={`Fotos da Nota Fiscal: ${nfdescarganf}`}
        iddescarganf={iddescarganf}
        idposto={idposto}
        nfdescarganf={nfdescarganf}
        isUpdate={isUpdate}
      />
    </>
  );
}

export default OperacaoRecebimentoNf;
